import { useState } from 'react';
import { AttorneyResponseAction, UserRole, WriteOffStatus } from '@repo/types';
import { Icon, Card, CardHeader, CardBody } from '@repo/ui';
import { cn, dateUtil } from '@xtnd/web/shared/utils';
import { FileCategory, MatterInvoiceSummaryFile } from '../../types';
import { FileAttachments } from './FileAttachments';
import { AttachmentsSkeleton } from '../../pages/AttachmentsSkeleton';

interface LineItem {
  lineItemId: string;
  lineNumber: string;
  comment: string;
}

interface AttorneyResponseItemProps {
  appealAction: AttorneyResponseAction;
  lineItems?: LineItem[];
  attachments?: MatterInvoiceSummaryFile[];
  timekeeperName?: string;
  submittedBy?: string;
  submittedByRole?: UserRole;
  submittedAt: string;
  isSubmitted?: boolean;
  numberColumns?: 1 | 2 | 3 | 4;
  isLoadingExistingFiles?: boolean;
  writeOffStatus?: WriteOffStatus;
  delegateEmail?: string | null;
}

export const AttorneyResponseItem = ({
  appealAction,
  lineItems = [],
  attachments = [],
  submittedBy,
  submittedAt,
  isSubmitted = true,
  timekeeperName,
  submittedByRole,
  numberColumns = 3,
  isLoadingExistingFiles = false,
  writeOffStatus,
  delegateEmail,
}: AttorneyResponseItemProps) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [showAllLineItems, setShowAllLineItems] = useState(false);
  const isAppeal = appealAction === AttorneyResponseAction.APPEAL;
  const hasLineItems = lineItems && lineItems.length > 0;
  const MAX_INITIAL_LINE_ITEMS = 3;
  const shouldShowReadMore = hasLineItems && lineItems.length > MAX_INITIAL_LINE_ITEMS;
  const displayedLineItems = showAllLineItems
    ? lineItems
    : lineItems.slice(0, MAX_INITIAL_LINE_ITEMS);

  const toggleExpand = (lineItemId: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [lineItemId]: !prev[lineItemId],
    }));
  };

  const toggleShowAllLineItems = () => {
    setShowAllLineItems(prev => !prev);
  };
  const paymentSupportAttachments = attachments.filter(attachment => attachment.category === FileCategory.PAYMENT_SUPPORT);
  const appealsSupportAttachments = attachments.filter(attachment => attachment.category === FileCategory.APPEALS_SUPPORT);

  return (
    <div className="relative opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
      <div className="mx-auto">
        {/* Header Section */}
        <Card className="mb-6">
          <CardHeader
            className={cn(
              'border-gray-200 dark:border-xtnd-dark-600',
              !isAppeal && 'border-b-0 !mb-0 !pb-0'
            )}
          >
            <div className="p-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                    Attorney Response Details
                  </h1>
                  {isSubmitted ? (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center gap-1">
                      <Icon name="HiCheckCircle" size="sm" />
                      Submitted
                    </span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 flex items-center gap-1">
                      <Icon name="HiClock" size="sm" />
                      Pending
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-col items-start gap-1 mt-2">
                  <span className="flex items-center gap-1">Timekeeper: {timekeeperName}</span>
                  {submittedBy && (
                    <p className="flex items-center gap-1">
                      Submitted: {submittedBy}
                      <span className="text-xs flex items-center px-1.5 py-0.25 text-[10px] uppercase tracking-wider font-bold bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 rounded border border-blue-100 dark:border-blue-800">
                        {submittedByRole}
                      </span>
                      -{' '}
                      <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        {submittedAt && isSubmitted
                          ? dateUtil.formatDate(
                            new Date(submittedAt),
                            dateUtil.DateFormat.US_WITH_TIME
                          )
                          : 'N/A'}
                      </span>
                    </p>
                  )}
                  {delegateEmail && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Icon name="HiUser" size="sm" />
                      <span>Delegated to: {delegateEmail}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 ${isAppeal
                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
                    : 'bg-gray-100 dark:bg-xtnd-dark-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-xtnd-dark-600'
                    }`}
                >
                  {isAppeal ? (
                    <Icon name="HiExclamationCircle" size="sm" />
                  ) : (
                    <Icon name="HiDocumentText" size="sm" />
                  )}
                  {isSubmitted && <>ACTION:{' '}
                    {writeOffStatus
                      ? writeOffStatus.replace(/_/g, ' ')
                      : appealAction.replace('_', ' ')}
                  </>}
                </div>
              </div>
            </div>
          </CardHeader>

          {/* Body Content */}
          {isAppeal && (
            <CardBody className="py-4 space-y-4 ">
              {/* Line Items Section */}
              <div className="space-y-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-xtnd-dark dark:text-xtnd-light ml-1">
                  <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center">
                    <Icon
                      name="HiHashtag"
                      size="md"
                      className="text-xtnd-blue dark:text-xtnd-blue-400"
                    />
                  </div>
                  Line Items ({lineItems.length})
                </h2>

                {hasLineItems ? (
                  <>
                    <div className="grid gap-4">
                      {displayedLineItems.map(item => {
                        const commentLines = item.comment?.split('\n') || [];
                        const shouldShowExpand = commentLines.length > 3;
                        const isExpanded = expandedItems[item.lineItemId] || false;

                        return (
                          <div
                            key={item.lineItemId}
                            className="bg-xtnd-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-600 rounded-xl overflow-hidden transition-all hover:shadow-md"
                          >
                            <div className="p-4 md:p-5">
                              {/* Line Number Header */}
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-bold text-xtnd-dark dark:text-xtnd-white uppercase tracking-widest">
                                    Line No #{item.lineNumber}
                                  </span>
                                </div>
                              </div>

                              {/* Comments Section */}
                              <div className="rounded-lg p-4 border border-xtnd-light dark:border-xtnd-dark-600">
                                <div className="flex items-start gap-3">
                                  <Icon
                                    name="HiChat"
                                    size="md"
                                    className="mt-1 text-xtnd-dark-400 dark:text-xtnd-light-400 shrink-0"
                                  />
                                  <div className="w-full">
                                    <p className="text-[10px] font-black text-xtnd-dark-400 dark:text-xtnd-light-400 uppercase tracking-wider mb-2">
                                      Comments
                                    </p>
                                    {item.comment ? (
                                      <>
                                        <div
                                          className={`text-xtnd-dark dark:text-xtnd-light text-sm whitespace-pre-line leading-relaxed ${!isExpanded && shouldShowExpand ? 'line-clamp-3' : ''}`}
                                        >
                                          {item.comment}
                                        </div>
                                        {shouldShowExpand && (
                                          <button
                                            onClick={() => toggleExpand(item.lineItemId)}
                                            className="mt-3 text-xs font-medium text-xtnd-blue dark:text-xtnd-blue-400 hover:text-xtnd-blue-600 dark:hover:text-xtnd-blue-300 flex items-center gap-1 transition-colors"
                                          >
                                            {isExpanded ? 'Show less' : 'Read full comment'}
                                            <Icon
                                              name={isExpanded ? 'HiChevronUp' : 'HiChevronRight'}
                                              size="sm"
                                              className="transform transition-transform"
                                            />
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-xtnd-dark-400 dark:text-xtnd-light-400 italic text-sm">
                                        -
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Read More Button */}
                    {shouldShowReadMore && (
                      <div className="flex justify-center pt-2">
                        <button
                          onClick={toggleShowAllLineItems}
                          className="flex items-center gap-2 px-4 py-2 text-sm  hover:text-xtnd-blue-600 dark:hover:text-xtnd-blue-300  transition-colors"
                        >
                          {showAllLineItems ? (
                            <>
                              <span>Show less</span>
                              <Icon name="HiChevronUp" size="sm" />
                            </>
                          ) : (
                            <>
                              <span>
                                Read more ({lineItems.length - MAX_INITIAL_LINE_ITEMS} more)
                              </span>
                              <Icon name="HiChevronDown" size="sm" />
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="border border-dashed border-xtnd-light dark:border-xtnd-dark-600 rounded-lg p-12 flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center w-16 h-16 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-full mb-4">
                      <Icon
                        name="HiDocumentText"
                        size="lg"
                        className="text-xtnd-dark-400 dark:text-xtnd-light-400"
                      />
                    </div>
                    <p className="text-base font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      No Data Available
                    </p>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-light-400 text-center">
                      This response contains no appeal items.
                    </p>
                  </div>
                )}
              </div>

              {/* Attachments Section */}
              <div className="space-y-4">
                {/* Header */}
                <h2 className="flex items-center gap-2 text-lg font-semibold text-xtnd-dark dark:text-xtnd-light ml-1">
                  <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center">
                    <Icon
                      name="HiPaperClip"
                      size="md"
                      className="text-xtnd-blue dark:text-xtnd-blue-400"
                    />
                  </div>
                  Attachments ({attachments.length})
                </h2>

                {/* CONTENT */}
                {isLoadingExistingFiles ? (
                  <AttachmentsSkeleton />
                ) : (
                  <div className="space-y-4">
                    {/* Appeals Support */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                        Appeals Support
                      </p>
                    </div>
                    {appealsSupportAttachments.length > 0 ? (
                      <FileAttachments
                        files={appealsSupportAttachments}
                        showCheckbox={false}
                        columns={1}
                      />
                    ) : (
                      <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Icon
                            name="HiDocumentReport"
                            size="sm"
                            className="text-gray-500 dark:text-xtnd-light"
                          />
                          <p className="text-sm text-gray-500 dark:text-xtnd-light">
                            No documents available
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Payment Support */}
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                        Payment Support
                      </p>
                    </div>
                    {paymentSupportAttachments.length > 0 ? (
                      <FileAttachments
                        files={paymentSupportAttachments}
                        showCheckbox={false}
                        columns={1}
                      />
                    ) : (
                      <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Icon
                            name="HiDocumentReport"
                            size="sm"
                            className="text-gray-500 dark:text-xtnd-light"
                          />
                          <p className="text-sm text-gray-500 dark:text-xtnd-light">
                            No documents available
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardBody>
          )}
        </Card>
      </div>
    </div>
  );
};
